import { ref, computed, onMounted, onBeforeUnmount, type ComputedRef } from 'vue'

/** Breakpoints alineados con los `--breakpoint-*` de `src/style.css`. */
export const BREAKPOINTS = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
} as const

export type BreakpointLayout = 'mobile' | 'tablet' | 'desktop'

/**
 * Layout actual según el ancho del viewport, reactivo a `resize`.
 *
 * `AppLayout` y `Dialog` necesitan saber en qué modo están para decidir entre
 * bottom-nav y sidebar, y si un diálogo va a pantalla completa. Leer
 * `window.innerWidth` dentro de un `computed` no basta: sin una fuente reactiva
 * el valor se cachea para siempre y el layout nunca cambia al redimensionar.
 */
const width = ref(readWidth())
let listeners = 0
let detach: (() => void) | null = null

function readWidth(): number {
  return typeof window === 'undefined' ? BREAKPOINTS.desktop : window.innerWidth
}

function onResize(): void {
  width.value = readWidth()
}

function layoutFor(value: number): BreakpointLayout {
  if (value < BREAKPOINTS.tablet) return 'mobile'
  if (value < BREAKPOINTS.desktop) return 'tablet'
  return 'desktop'
}

/**
 * Devuelve un `ComputedRef<BreakpointLayout>` sincronizado con el tamaño de la
 * ventana. Compartido entre componentes: solo un listener por instancia de la
 * app, activado mientras al menos un componente lo use.
 */
export function useBreakpoint(): ComputedRef<BreakpointLayout> {
  // Lectura síncrona para que el primer render ya use el viewport real.
  onResize()

  onMounted(() => {
    if (listeners === 0) {
      window.addEventListener('resize', onResize, { passive: true })
      window.addEventListener('orientationchange', onResize, { passive: true })
      detach = () => {
        window.removeEventListener('resize', onResize)
        window.removeEventListener('orientationchange', onResize)
      }
    }
    listeners += 1
  })

  onBeforeUnmount(() => {
    listeners -= 1
    if (listeners <= 0) {
      listeners = 0
      detach?.()
      detach = null
    }
  })

  return computed(() => layoutFor(width.value))
}